import type { Metadatos } from './consultas'

/**
 * ⚠️ Las fechas no vienen todas en el mismo huso. En clientes salen de
 * `created_at`, que es `timestamptz`, y traen desplazamiento. En leads no hay
 * columna: la fecha se lee de la cabecera del texto, que esta escrita en hora
 * local espanola y llega sin desplazamiento. `new Date` interpreta la primera
 * como instante absoluto y la segunda como hora local, que es justo lo que hace
 * falta en cada caso.
 */
const FECHA_LARGA = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const HORA = new Intl.DateTimeFormat('es-ES', { hour: '2-digit', minute: '2-digit' })

function aFecha(valor: string | null | undefined): Date | null {
  if (!valor) return null
  const fecha = new Date(valor)
  return Number.isNaN(fecha.getTime()) ? null : fecha
}

export function formatearFecha(valor: string | null | undefined): string | null {
  const fecha = aFecha(valor)
  return fecha ? FECHA_LARGA.format(fecha) : null
}

export function formatearHora(valor: string | null | undefined): string | null {
  const fecha = aFecha(valor)
  return fecha ? HORA.format(fecha) : null
}

/**
 * Nombres de los campos de `meta`. Solo estan los que no se leen bien al
 * quitarles el guion bajo: el resto sale de la propia clave, porque hay tres
 * esquemas distintos (10, 13 y 21 campos) y crecen sin avisar. Una lista
 * cerrada dejaria de mostrar los campos nuevos en silencio.
 */
const NOMBRES: Record<string, string> = {
  cp: 'Codigo postal',
  dni_cif: 'DNI o CIF',
  iban: 'IBAN',
  url_maps: 'Enlace de mapas',
  datos_cliente: 'Datos del cliente',
  accion_solicitada: 'Accion solicitada',
}

export function etiquetaCampo(clave: string): string {
  const nombre = NOMBRES[clave]
  if (nombre) return nombre
  const texto = clave.replace(/_/g, ' ')
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}

/**
 * El texto con el que se ensena un valor de `meta`, o `null` si no hay forma
 * honesta de ensenarlo. Nunca se serializa a JSON: volcar `{"a": 1}` en la
 * pantalla es exactamente el defecto que se esta corrigiendo.
 */
export function valorLegible(valor: unknown): string | null {
  if (typeof valor === 'string') return valor.trim() || null
  if (typeof valor === 'number' && Number.isFinite(valor)) return String(valor)
  if (typeof valor === 'boolean') return valor ? 'Si' : 'No'
  if (Array.isArray(valor)) {
    const partes = valor.map(valorLegible).filter((parte): parte is string => parte !== null)
    return partes.length > 0 ? partes.join(', ') : null
  }
  return null
}

/** Claves que ya se pintan aparte y no deben repetirse en la lista de campos. */
const YA_PINTADAS = new Set(['estado_emocional', 'estado_animo', 'animo', 'sentimiento'])

export interface Campo {
  clave: string
  etiqueta: string
  valor: string
}

export function camposLegibles(meta: Metadatos | null | undefined): Campo[] {
  if (!meta || typeof meta !== 'object') return []
  const campos: Campo[] = []
  for (const [clave, valor] of Object.entries(meta)) {
    if (YA_PINTADAS.has(clave)) continue
    const texto = valorLegible(valor)
    if (texto === null) continue
    campos.push({ clave, etiqueta: etiquetaCampo(clave), valor: texto })
  }
  return campos
}
